import React, { type ChangeEvent, type FormEvent, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  InlineMessage,
  InputField,
  P,
  Tooltip,
  useTooltipText,
} from '@saviynt/design-system';

import { ConfirmationDialog, FormLayout } from '../../../shared/components';
import { validateTotpCodeAtLogin } from '../../../shared/data-sources';
import { baseUrl } from '../../../shared/utils';
import { api } from '../data-sources';

import './MfaSetupForm.css';

interface MfaRegistrationProps {
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * Displays MFA seed and accepts verification code.
 */
function MfaSetupForm({
  onCancel: handleCancel,
  onSuccess: handleContinue,
}: MfaRegistrationProps): JSX.Element {
  const [showQRCode, setShowQRCode] = useState(true);
  const [seed, setSeed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canContinue, setCanContinue] = useState<boolean>(false);
  const [codeInputText, setCodeInputText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {
    tooltipText,
    handleTooltipTriggerClick,
    handleTooltipClose,
    tooltipCloseDelayAfterClick,
  } = useTooltipText({
    initialText: 'Copy',
    updatedText: 'Copied',
    closeDelayAfterClick: 5000,
  });

  const handleToggleClick = async () => {
    setSeed(await api.getTotpSeedValue());
    setShowQRCode((prev) => !prev);
  };

  const handleSubmitClick: (e?: FormEvent<HTMLFormElement>) => void = (e) => {
    e?.preventDefault();
    setError(null);

    const unexpectedError = 'An unexpected error occurred. Please try again.';

    validateTotpCodeAtLogin(codeInputText)
      .then((isValid) => {
        if (isValid) {
          setShowConfirmation(true);
        } else {
          setError('Wrong code');
        }
      })
      .catch(() => {
        setError(unexpectedError);
      });
  };

  const handleCopyClick = async () => {
    await navigator.clipboard.writeText(seed ?? '');
    handleTooltipTriggerClick();
  };

  const handleCodeInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCodeInputText(e.target.value);
    setCanContinue(e.target.value.length === 6);
  };

  const handleShowText = () => {
    setShowQRCode(true);
  };

  return showConfirmation ? (
    <ConfirmationDialog
      title={`You're all set!`}
      subtitle='Your MFA enrollment is complete.'
      continueLabel={(countdown) => `Go to Saviynt (${countdown}s)`}
      onContinue={handleContinue}
    />
  ) : (
    <FormLayout
      title='MFA setup'
      footerContent={footer()}
      onSubmit={handleSubmitClick}>
      {showQRCode ? qrCodeFragment() : seedTextFragment()}

      <P className='Auth_MfaSetupForm-description'>
        Next, enter the verification code displayed on the app:
      </P>
      <InputField
        name='Auth_MfaSetupForm-verificationCode'
        ariaLabel='Verification code'
        size='large'
        type='text'
        placeholder=''
        onChange={handleCodeInputChange}
        value={codeInputText}
        setValue={setCodeInputText}
        maxLength={6}
        isCritical={!!error}
        CriticalHelperText={
          <InlineMessage
            text={error ?? ''}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        }
      />
    </FormLayout>
  );

  function footer() {
    return (
      <Box className='Auth_MfaSetupForm-footer'>
        {showQRCode ? (
          <Button
            onClick={handleCancel}
            kind='ghost'
            className='Auth_MfaSetupForm-footer-button'>
            Back
          </Button>
        ) : (
          <Button
            onClick={handleShowText}
            kind='ghost'
            className='Auth_MfaSetupForm-footer-button'>
            Back to QR Code
          </Button>
        )}

        <Button
          onClick={handleSubmitClick}
          isDisabled={!canContinue}
          className='Auth_MfaSetupForm-footer-button'
          kind='subtle'>
          Continue
        </Button>
      </Box>
    );
  }

  function qrCodeFragment() {
    return (
      <>
        <P className='Auth_MfaSetupForm-description'>
          To connect your authenticator app and start using time-based one-time
          passcodes (TOTP), scan this code.
        </P>
        <img
          className='Auth_MfaSetupForm-qrCode'
          src={`${baseUrl.authms}/totp/setup`}
          alt='QR Code with MFA TOTP seed value'
        />
        <Button
          kind='ghost'
          onClick={handleToggleClick}
          className='Auth_MfaSetupForm-cantScanButton'>
          Can&apos;t Scan
        </Button>
      </>
    );
  }

  function seedTextFragment() {
    return (
      <>
        <P className='Auth_MfaSetupForm-description'>
          On your mobile device, enter this code in the app:
        </P>
        <Box className='Auth_MfaSetupForm-seed'>
          <P>{seed}</P>
          <Tooltip
            text={tooltipText}
            placement='top'
            closeDelayAfterClick={tooltipCloseDelayAfterClick}
            onClose={handleTooltipClose}
            isDisabled={tooltipText === 'Copied'}
            trigger={
              <ButtonIcon
                size='medium'
                ariaLabel='Copy to clipboard'
                kind='outlinedSecondary'
                icon={<Icon kind='Copy' />}
                className='Auth_MfaSetupForm-copyButton'
                onClick={handleCopyClick}
              />
            }
          />
        </Box>
      </>
    );
  }
}

export default MfaSetupForm;
